import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import type { QuestionPurpose } from '@course-authoring/shared-types';
import { isAuthorWithAiQuestionType, type AuthorWithAiQuestionType } from '../../api/authorWithAiApi';
import { QUESTION_BANK_SORTS } from '../../components/questionBankBrowse/QuestionBankSortRow';
import { PAGE_SIZE_OPTIONS, type AllowedPageSize } from '../../components/table/ListPagination';
import { useClientContext } from '../../hooks/useClientContext';

/** The Question Bank page's committed filters, as held in the query string. */
export interface QuestionBankFilterValues {
  /** The committed search term ('' when none). */
  q: string;
  conceptId: number | null;
  blockType: AuthorWithAiQuestionType;
  purpose: QuestionPurpose;
  sortKey: string;
  /** Checked framework labels, in the order they were checked. */
  labelIds: readonly number[];
  page: number;
  pageSize: AllowedPageSize;
}

export type QuestionBankFilterPatch = Partial<QuestionBankFilterValues>;

const PURPOSES: readonly QuestionPurpose[] = ['Sentiment', 'Knowledge', 'Reflection', 'Survey'];

/** A type's purpose when the URL names none: the derivation rules' first (Sentiment for Likert Scale, Knowledge otherwise). */
export const defaultPurposeFor = (blockType: AuthorWithAiQuestionType): QuestionPurpose =>
  (blockType === 'likert-scale' ? 'Sentiment' : 'Knowledge');

export const QUESTION_BANK_OPENING: QuestionBankFilterValues = {
  q: '',
  conceptId: null,
  blockType: 'likert-scale',
  purpose: 'Sentiment',
  sortKey: 'responses',
  labelIds: [],
  page: 1,
  pageSize: 25,
};

const positiveId = (value: string | null): number | null =>
  value !== null && /^[1-9]\d*$/.test(value) && Number.isSafeInteger(Number(value)) ? Number(value) : null;

const isPurpose = (value: string | null): value is QuestionPurpose => value !== null && (PURPOSES as readonly string[]).includes(value);

export function readQuestionBankFilters(params: URLSearchParams): QuestionBankFilterValues {
  const type = params.get('type');
  const blockType = type !== null && isAuthorWithAiQuestionType(type) ? type : QUESTION_BANK_OPENING.blockType;
  const purpose = params.get('purpose');
  const sort = params.get('sort');
  const per = Number(params.get('per'));
  const labelIds = [...new Set(params.getAll('label').map(positiveId).filter((id): id is number => id !== null))];
  return {
    q: params.get('q')?.trim() ?? '',
    conceptId: positiveId(params.get('concept')),
    blockType,
    purpose: isPurpose(purpose) ? purpose : defaultPurposeFor(blockType),
    sortKey: QUESTION_BANK_SORTS.some((option) => option.key === sort) ? sort! : QUESTION_BANK_OPENING.sortKey,
    labelIds,
    page: positiveId(params.get('page')) ?? 1,
    pageSize: (PAGE_SIZE_OPTIONS as readonly number[]).includes(per) ? per as AllowedPageSize : QUESTION_BANK_OPENING.pageSize,
  };
}

/** Values equal to the opening state (a type's own default purpose included) are left out. */
export function writeQuestionBankFilters(values: QuestionBankFilterValues): URLSearchParams {
  const params = new URLSearchParams();
  if (values.q) params.set('q', values.q);
  if (values.conceptId !== null) params.set('concept', String(values.conceptId));
  if (values.blockType !== QUESTION_BANK_OPENING.blockType) params.set('type', values.blockType);
  if (values.purpose !== defaultPurposeFor(values.blockType)) params.set('purpose', values.purpose);
  if (values.sortKey !== QUESTION_BANK_OPENING.sortKey) params.set('sort', values.sortKey);
  for (const labelId of values.labelIds) params.append('label', String(labelId));
  if (values.page !== 1) params.set('page', String(values.page));
  if (values.pageSize !== QUESTION_BANK_OPENING.pageSize) params.set('per', String(values.pageSize));
  return params;
}

/** Any of these changes the result set, so the list returns to page 1; the sort keeps the page (writeup). */
const RESETS_PAGE: ReadonlyArray<keyof QuestionBankFilterValues> = ['q', 'conceptId', 'blockType', 'purpose', 'labelIds', 'pageSize'];

/** What the page stamps on each history entry it writes: the content context the entry's filters belong to. */
interface QuestionBankEntryState {
  questionBankContextId: string | null;
}

/** The context an entry was stamped with; `undefined` for an entry the page did not write (a link, the nav). */
const stampedContextId = (state: unknown): string | null | undefined =>
  (state !== null && typeof state === 'object' && 'questionBankContextId' in state
    ? (state as QuestionBankEntryState).questionBankContextId
    : undefined);

/**
 * Filter state for the Question Bank page, held in the query string (`q`,
 * `concept`, `type`, `purpose`, `sort`, `label` repeated, `page`, `per`) so
 * a filtered view can be linked and survives reload. Every change pushes a
 * history entry, so browser back steps back through them. Each entry is
 * stamped (history state) with the content context its filters belong to;
 * an entry holding another context's filters, whether reached by a context
 * switch or by back or forward across one, reads as the opening state and
 * is cleared in place, so back never restores another context's filters.
 */
export function useQuestionBankFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { clientContext } = useClientContext();
  const contextId = clientContext?.id ?? null;
  const stamp = useMemo((): QuestionBankEntryState => ({ questionBankContextId: contextId }), [contextId]);
  const entryContextId = stampedContextId(useLocation().state);
  const foreign = entryContextId !== undefined && entryContextId !== contextId;

  const filters = useMemo(() => (foreign ? QUESTION_BANK_OPENING : readQuestionBankFilters(searchParams)), [foreign, searchParams]);

  const update = useCallback((patch: QuestionBankFilterPatch, options: { replace?: boolean } = {}) => {
    setSearchParams((current) => {
      const merged = { ...(foreign ? QUESTION_BANK_OPENING : readQuestionBankFilters(current)), ...patch };
      if (patch.page === undefined && RESETS_PAGE.some((key) => key in patch)) merged.page = 1;
      return writeQuestionBankFilters(merged);
    }, { replace: options.replace ?? false, state: stamp });
  }, [setSearchParams, stamp, foreign]);

  useEffect(() => {
    if (entryContextId === contextId) return;
    if (entryContextId === undefined) {
      // An entry the page did not write (a link, the nav): keep its filters, stamp it with the context they apply to.
      setSearchParams((current) => current, { replace: true, state: stamp });
      return;
    }
    setSearchParams(new URLSearchParams(), { replace: true, state: stamp });
  }, [entryContextId, contextId, setSearchParams, stamp]);

  return { filters, update };
}
